import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { SUBSCRIPTIONS, SUPPORT_INFO } from '@/data/subscriptions';
import { ExternalLink, Check, X, ChevronDown, ChevronUp, AlertTriangle } from 'lucide-react';

const SubscriptionScreen = () => {
  const [expandedFaq, setExpandedFaq] = useState(null);

  const handleSubscribe = (paypalLink, planName) => {
    // Direkt zu PayPal weiterleiten
    window.open(paypalLink, '_blank', 'noopener,noreferrer');
  };

  const goToWebsite = () => {
    window.open(SUPPORT_INFO.websiteUrl, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="min-h-screen bg-background pb-24">
      {/* Top Banner - Important Notice */}
      <div className="bg-primary text-primary-foreground px-4 py-4">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-medium leading-tight">
              Einzelbuchungen & Zusatzkurse sind nicht in der App buchbar
            </p>
          </div>
        </div>
        <Button 
          onClick={goToWebsite}
          className="w-full mt-3 bg-background text-foreground hover:bg-background/90"
          size="lg"
        >
          <ExternalLink className="w-4 h-4 mr-2" />
          Zur Website wechseln
        </Button>
      </div>

      <div className="px-4 pt-6">
        {/* Logo */}
        <div className="w-28 h-28 mx-auto mb-4 rounded-full overflow-hidden border-2 border-primary shadow-glow">
          <img 
            src="/logo.jpg" 
            alt="S&T Brain Trainer Logo" 
            className="w-full h-full object-cover"
          />
        </div>
        
        {/* Branding */}
        <h1 className="text-2xl font-display font-bold text-foreground text-center mb-2">
          S&T Brain Trainer
        </h1>
        <p className="text-muted-foreground text-sm text-center mb-4">
          Dein Weg zum Industriemeister
        </p>
        
        {/* Subject Overview */}
        <div className="text-center text-sm text-muted-foreground mb-6 space-y-1">
          <p><span className="text-primary font-semibold">BQ:</span> {SUPPORT_INFO.subjects.bq}</p>
          <p><span className="text-primary font-semibold">HQ:</span> {SUPPORT_INFO.subjects.hq}</p>
          <p><span className="text-primary font-semibold">HQ Technik:</span> {SUPPORT_INFO.subjects.hqTechnik}</p>
        </div>

        {/* Important Notice */}
        <Card className="border-2 border-dashed border-primary/50 bg-transparent mb-6">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold text-foreground text-sm mb-1">
                  Wichtiger Hinweis
                </h3>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  An Feiertagen findet kein Unterricht statt. In der Urlaubszeit kann es ebenfalls vorkommen, dass der Unterricht ausfällt. Änderungen werden in unserer WhatsApp-Gruppe bekannt gegeben.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Subscription Plans */}
        <div className="space-y-4">
          {SUBSCRIPTIONS.map((plan, index) => (
            <Card 
              key={plan.id}
              className={`overflow-hidden animate-slide-up ${
                plan.variant === 'tech'
                  ? 'border-2 border-dashed border-primary/50'
                  : plan.popular 
                    ? 'border-2 border-primary bg-primary text-primary-foreground' 
                    : 'border-2 border-primary'
              }`}
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <CardContent className="p-5">
                {/* Plan Name */}
                <h3 className={`text-xl font-bold text-center mb-2 ${
                  plan.popular ? 'text-primary-foreground' : 'text-foreground'
                }`}>
                  {plan.name}
                </h3>
                
                {/* Price */}
                <div className="text-center mb-4">
                  <span className={`text-4xl font-bold ${
                    plan.popular ? 'text-primary-foreground' : 'text-primary'
                  }`}>
                    {plan.price}
                  </span>
                </div>

                {/* Schedule if exists */}
                {plan.schedule && (
                  <div className={`text-center text-xs mb-4 p-2 rounded-lg ${
                    plan.popular ? 'bg-background/10' : 'bg-primary/5'
                  }`}>
                    <p className={`whitespace-pre-line ${
                      plan.popular ? 'text-primary-foreground/80' : 'text-muted-foreground'
                    }`}>
                      {plan.schedule}
                    </p>
                  </div>
                )}

                {/* Features */}
                <ul className="space-y-2 mb-5">
                  {plan.features.map((feature, i) => (
                    <li key={i} className={`flex items-center gap-2 text-sm ${
                      plan.popular ? 'text-primary-foreground/90' : 'text-muted-foreground'
                    }`}>
                      <Check className={`w-4 h-4 flex-shrink-0 ${
                        plan.popular ? 'text-primary-foreground' : 'text-success'
                      }`} />
                      {feature}
                    </li>
                  ))}
                  
                  {/* Exclusions */}
                  {plan.exclusions && plan.exclusions.map((exclusion, i) => (
                    <li key={`ex-${i}`} className={`flex items-center gap-2 text-sm ${
                      plan.popular ? 'text-primary-foreground/70' : 'text-muted-foreground/70'
                    }`}>
                      <X className="w-4 h-4 flex-shrink-0 text-destructive" />
                      {exclusion}
                    </li>
                  ))}
                </ul>

                {/* CTA Button */}
                <Button 
                  onClick={() => handleSubscribe(plan.paypalLink, plan.name)}
                  className={`w-full ${
                    plan.popular 
                      ? 'bg-background text-foreground hover:bg-background/90' 
                      : ''
                  }`}
                  variant={plan.popular ? 'secondary' : 'default'}
                  size="lg"
                >
                  {plan.buttonText || 'Abo starten'}
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* FAQ Section */}
        <div className="mt-8">
          <h3 className="text-lg font-semibold text-foreground mb-4">
            Häufig gestellte Fragen
          </h3>

          <div className="space-y-2">
            {SUPPORT_INFO.faq.map((item, index) => (
              <Card key={index} className="border-border overflow-hidden">
                <button
                  onClick={() => setExpandedFaq(expandedFaq === index ? null : index)}
                  className="w-full p-4 flex items-center justify-between text-left"
                >
                  <span className="font-medium text-foreground pr-4 text-sm">{item.question}</span>
                  {expandedFaq === index ? (
                    <ChevronUp className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                  )}
                </button>
                {expandedFaq === index && (
                  <div className="px-4 pb-4">
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {item.answer}
                    </p>
                  </div>
                )}
              </Card>
            ))}
          </div>
        </div>

        {/* ProvenExpert Bewertungssiegel */}
        <div className="mt-8 flex justify-center">
          <a 
            href="https://www.provenexpert.com/st-brain-trainer/?utm_source=Widget&utm_medium=Widget&utm_campaign=Widget" 
            title="Kundenbewertungen & Erfahrungen zu S&T Brain-Trainer. Mehr Infos anzeigen." 
            target="_blank" 
            rel="noopener noreferrer"
            className="block"
          >
            <img 
              src="https://images.provenexpert.com/0b/74/2505acd20d9c48f78317f338d22f/widget_portrait_105_de_0.png" 
              alt="Erfahrungen & Bewertungen zu S&T Brain-Trainer" 
              width="105" 
              height="126" 
              className="border-0 mx-auto"
            />
          </a>
        </div>

        {/* Footer */}
        <div className="mt-8 text-center pb-4">
          <p className="text-xs text-muted-foreground">
            © S&T Brain Trainer · Lernen mit System
          </p>
          <a 
            href={SUPPORT_INFO.websiteUrl} 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-xs text-primary hover:underline mt-1 inline-block"
          >
            www.stbrain-trainerimgbr.de
          </a>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionScreen;
